import { AxiosError } from "axios";
import {
  Download,
  FileText,
  Loader2,
  Lock,
  MessageCircle,
  Paperclip,
  PanelLeftClose,
  PanelLeftOpen,
  Send,
  Settings2,
  Trash2,
  UserMinus,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isRequestCancellation } from "@shared/api/client";
import {
  addGroupMember,
  createGroupChat,
  deleteGroupChat,
  downloadGroupAttachment,
  getGroupChatUsers,
  getGroupMessages,
  markGroupRead,
  removeGroupMember,
  renameGroupChat,
  sendGroupMessage,
  setGroupMessaging,
  type GroupChatGroup,
  type GroupChatMessage,
  type GroupChatScope,
  type GroupChatUser,
} from "@shared/api/groupChat";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { useGroupChatHub } from "./ChatHub";

type Props = {
  scope: GroupChatScope;
  floating?: boolean;
  active: boolean;
  showConversationList: boolean;
  onToggleConversationList: () => void;
};

type ConfirmState =
  | { kind: "delete-group"; group: GroupChatGroup }
  | { kind: "remove-member"; group: GroupChatGroup; user: GroupChatUser }
  | null;

type MessageLoadOptions = {
  quiet?: boolean;
  incremental?: boolean;
};

const GROUP_REFRESH_INTERVAL_MS = 4_000;
const GROUP_RECONCILE_EVERY_TICKS = 6;
const GROUP_INCREMENTAL_OVERLAP = 100;
const GROUP_SEND_RETRY_DELAYS_MS = [700, 1_500] as const;

function roleLabel(role: string) {
  return role.replaceAll("_", " ").replaceAll("-", " ");
}

function timeLabel(value: string | null) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function apiMessage(error: unknown, fallback: string) {
  return error instanceof AxiosError
    ? ((error.response?.data as { message?: string })?.message ?? fallback)
    : fallback;
}

function messageOrder(message: GroupChatMessage) {
  return message.client_order ?? new Date(message.creado_en).getTime();
}

function mergeMessages(...collections: GroupChatMessage[][]): GroupChatMessage[] {
  const stored = new Map<number, GroupChatMessage>();
  const storedClientIds = new Set<string>();
  const pending = new Map<string, GroupChatMessage>();
  const flat = collections.flat();

  flat.forEach((message) => {
    if (message.id <= 0) return;
    stored.set(message.id, message);
    if (message.client_id) storedClientIds.add(message.client_id);
  });

  flat.forEach((message) => {
    if (message.id > 0) return;
    const key = message.client_id ?? String(message.id);
    if (message.client_id && storedClientIds.has(message.client_id)) return;
    pending.set(key, message);
  });

  return [...stored.values(), ...pending.values()].sort((a, b) => {
    const orderDiff = messageOrder(a) - messageOrder(b);
    return orderDiff !== 0 ? orderDiff : a.id - b.id;
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function latestStoredMessageId(messages: GroupChatMessage[]) {
  return messages.reduce((latest, message) => (message.id > latest ? message.id : latest), 0);
}

function formatFileSize(size?: number | null) {
  if (!size) return "Archivo";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

export function GroupChatWorkspace({
  scope,
  floating = false,
  active,
  showConversationList,
  onToggleConversationList,
}: Props) {
  const admin = scope === "admin";
  const {
    groups,
    loadingGroups,
    selectedGroupId,
    selectGroup,
    refreshGroups,
    patchGroup,
    createRequestId,
    setCreateBusy,
  } = useGroupChatHub();

  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [openingManager, setOpeningManager] = useState(false);
  const [managerSaving, setManagerSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [openingCreate, setOpeningCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [allUsers, setAllUsers] = useState<GroupChatUser[]>([]);
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [blockMode, setBlockMode] = useState<"active" | "indefinite" | "until">("active");
  const [blockedUntil, setBlockedUntil] = useState("");
  const [memberUpdating, setMemberUpdating] = useState(false);

  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const forceScrollToBottomRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedIdRef = useRef<number | null>(null);
  const messagesCacheRef = useRef<Record<number, GroupChatMessage[]>>({});
  const loadingMessagesRef = useRef<Record<number, boolean>>({});
  const sendingIdsRef = useRef<Set<string>>(new Set());
  const allUsersRef = useRef<GroupChatUser[]>([]);
  const usersPromiseRef = useRef<Promise<GroupChatUser[]> | null>(null);
  const managerOpeningRef = useRef(false);
  const handledCreateRequestRef = useRef(0);
  const refreshTickRef = useRef<Record<number, number>>({});

  const selected = groups.find((group) => group.id === selectedGroupId) ?? null;

  const updateCachedMessages = useCallback(
    (
      groupId: number,
      updater: (current: GroupChatMessage[]) => GroupChatMessage[],
    ) => {
      const next = updater(messagesCacheRef.current[groupId] ?? []);
      messagesCacheRef.current[groupId] = next;
      if (selectedIdRef.current === groupId) setMessages(next);
      return next;
    },
    [],
  );

  const loadMessages = useCallback(
    async (groupId: number, options: MessageLoadOptions = {}) => {
      const quiet = options.quiet === true;
      const incremental = options.incremental === true;
      if (loadingMessagesRef.current[groupId]) return;

      const cached = messagesCacheRef.current[groupId] ?? [];
      const latestId = latestStoredMessageId(cached);
      const afterId = incremental
        ? Math.max(0, latestId - GROUP_INCREMENTAL_OVERLAP)
        : null;

      loadingMessagesRef.current[groupId] = true;
      if (!quiet && cached.length === 0) setLoadingMessages(true);

      try {
        const data = await getGroupMessages(scope, groupId, afterId);
        const recoveredClientIds = new Set(
          data.messages
            .map((message) => message.client_id)
            .filter((clientId): clientId is string => Boolean(clientId)),
        );
        const recoveredFailedMessage = cached.some(
          (message) =>
            message.status === "error" &&
            Boolean(message.client_id) &&
            recoveredClientIds.has(message.client_id as string),
        );
        const next = mergeMessages(cached, data.messages);

        messagesCacheRef.current[groupId] = next;
        if (selectedIdRef.current === groupId) setMessages(next);

        patchGroup(groupId, {
          unread_count: 0,
          messages_enabled: data.group.enabled,
          blocked_until: data.group.blocked_until,
          blocked_reason: data.group.reason,
          ...(data.messages.length > 0
            ? {
                last_message: data.messages[data.messages.length - 1]?.mensaje ?? "Sin mensajes todavía",
                last_message_at: data.messages[data.messages.length - 1]?.creado_en ?? null,
              }
            : {}),
        });

        if (!incremental || data.messages.length > 0) {
          void markGroupRead(scope, groupId).catch((readError) => {
            if (!isRequestCancellation(readError)) {
              console.error("No se pudo marcar el grupo como leído", readError);
            }
          });
        }

        if (!quiet || recoveredFailedMessage) setError(null);
      } catch (requestError) {
        if (!isRequestCancellation(requestError) && !quiet) {
          setError("No se pudo cargar la conversación del grupo.");
        }
      } finally {
        loadingMessagesRef.current[groupId] = false;
        if (selectedIdRef.current === groupId) setLoadingMessages(false);
      }
    },
    [patchGroup, scope],
  );

  useEffect(() => {
    selectedIdRef.current = selectedGroupId;

    if (!active || !selectedGroupId) {
      setLoadingMessages(false);
      return;
    }

    const cached = messagesCacheRef.current[selectedGroupId] ?? [];
    shouldStickToBottomRef.current = true;
    forceScrollToBottomRef.current = true;
    setMessages(cached);
    setError(null);
    void loadMessages(selectedGroupId, {
      quiet: cached.length > 0,
      incremental: cached.length > 0,
    });
  }, [active, loadMessages, selectedGroupId]);

  useEffect(() => {
    if (!active || !selectedGroupId) return;
    refreshTickRef.current[selectedGroupId] = 0;

    const syncVisibleGroup = (forceFull = false) => {
      const groupId = selectedIdRef.current;
      if (!groupId || groupId !== selectedGroupId) return;
      const nextTick = (refreshTickRef.current[groupId] ?? 0) + 1;
      refreshTickRef.current[groupId] = nextTick;
      const fullReconcile = forceFull || nextTick % GROUP_RECONCILE_EVERY_TICKS === 0;
      void loadMessages(groupId, {
        quiet: true,
        incremental: !fullReconcile,
      });
    };

    const timer = window.setInterval(() => syncVisibleGroup(false), GROUP_REFRESH_INTERVAL_MS);
    const handleFocus = () => syncVisibleGroup(true);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") syncVisibleGroup(true);
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [active, loadMessages, selectedGroupId]);

  useEffect(() => {
    if (!active) return;
    const container = messagesScrollRef.current;
    if (!container) return;

    const force = forceScrollToBottomRef.current;
    if (!force && !shouldStickToBottomRef.current) return;

    const frame = window.requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: force ? "auto" : "smooth",
      });
      forceScrollToBottomRef.current = false;
      shouldStickToBottomRef.current = true;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [active, messages.length, selectedGroupId]);

  const handleMessagesScroll = () => {
    const container = messagesScrollRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom <= 96;
  };

  useEffect(() => {
    setManagerOpen(false);
    setOpeningManager(false);
    managerOpeningRef.current = false;
  }, [selectedGroupId]);

  const ensureUsers = useCallback(async (): Promise<GroupChatUser[]> => {
    if (!admin) return [];
    if (allUsersRef.current.length > 0) return allUsersRef.current;
    if (usersPromiseRef.current) return usersPromiseRef.current;

    const promise = getGroupChatUsers()
      .then((users) => {
        allUsersRef.current = users;
        setAllUsers(users);
        return users;
      })
      .finally(() => {
        usersPromiseRef.current = null;
      });

    usersPromiseRef.current = promise;
    return promise;
  }, [admin]);

  const openCreate = useCallback(async () => {
    if (!admin || openingCreate || creating || createOpen) {
      setCreateBusy(false);
      return;
    }

    setOpeningCreate(true);
    setError(null);

    try {
      await ensureUsers();
      setGroupName("");
      setSelectedUsers([]);
      setCreateOpen(true);
    } catch (requestError) {
      if (!isRequestCancellation(requestError)) {
        setError("No se pudo cargar el listado de usuarios.");
      }
    } finally {
      setOpeningCreate(false);
      setCreateBusy(false);
    }
  }, [admin, createOpen, creating, ensureUsers, openingCreate, setCreateBusy]);

  useEffect(() => {
    if (!admin || createRequestId <= handledCreateRequestRef.current) return;
    handledCreateRequestRef.current = createRequestId;
    void openCreate();
  }, [admin, createRequestId, openCreate]);

  const create = async () => {
    if (creating || !groupName.trim() || selectedUsers.length === 0) return;

    setCreating(true);
    setCreateBusy(true);
    setError(null);

    try {
      const groupId = await createGroupChat(groupName.trim(), selectedUsers);
      setCreateOpen(false);
      const refreshed = await refreshGroups(true);
      if (!refreshed.some((group) => group.id === groupId)) {
        await refreshGroups(true);
      }
      selectGroup(groupId);
    } catch (requestError) {
      if (!isRequestCancellation(requestError)) {
        setError(apiMessage(requestError, "No se pudo crear el grupo."));
      }
    } finally {
      setCreating(false);
      setCreateBusy(false);
    }
  };

  const openManager = async () => {
    if (!selected?.is_creator || managerOpeningRef.current || managerOpen) return;

    managerOpeningRef.current = true;
    setOpeningManager(true);
    setError(null);

    setGroupName(selected.nombre);
    setBlockMode(
      selected.messages_enabled ? "active" : selected.blocked_until ? "until" : "indefinite",
    );
    setBlockedUntil(
      selected.blocked_until
        ? new Date(selected.blocked_until).toISOString().slice(0, 16)
        : "",
    );
    setManagerOpen(true);

    try {
      await ensureUsers();
    } catch (requestError) {
      if (!isRequestCancellation(requestError)) {
        setError(
          "La configuración se abrió, pero no se pudo actualizar el listado de usuarios para añadir miembros.",
        );
      }
    } finally {
      managerOpeningRef.current = false;
      setOpeningManager(false);
    }
  };

  const sendPendingMessage = useCallback(
    async (
      groupId: number,
      clientId: string,
      clientOrder: number,
      messageText: string,
      currentFile: File | null,
    ) => {
      if (sendingIdsRef.current.has(clientId)) return;
      sendingIdsRef.current.add(clientId);

      let lastError: unknown = null;

      try {
        for (let attempt = 0; attempt <= GROUP_SEND_RETRY_DELAYS_MS.length; attempt += 1) {
          try {
            const stored = await sendGroupMessage(
              scope,
              groupId,
              messageText,
              clientId,
              currentFile,
            );

            const resolved: GroupChatMessage = {
              ...stored,
              client_id: clientId,
              client_order: clientOrder,
              status: "sent",
            };

            const next = updateCachedMessages(groupId, (current) =>
              mergeMessages(
                current.filter((message) => message.client_id !== clientId),
                [resolved],
              ),
            );
            const latest = next[next.length - 1];
            if (latest) {
              patchGroup(groupId, {
                last_message: latest.mensaje,
                last_message_at: latest.creado_en,
                unread_count: 0,
              });
            }

            setError(null);
            window.setTimeout(() => {
              void loadMessages(groupId, { quiet: true, incremental: true });
            }, 450);
            window.setTimeout(() => {
              void loadMessages(groupId, { quiet: true, incremental: false });
            }, 1_800);
            return;
          } catch (requestError) {
            if (isRequestCancellation(requestError)) return;
            lastError = requestError;
            const retryDelay = GROUP_SEND_RETRY_DELAYS_MS[attempt];
            if (retryDelay === undefined) break;
            await delay(retryDelay);
          }
        }

        const next = updateCachedMessages(groupId, (current) =>
          current.map((message) =>
            message.client_id === clientId
              ? { ...message, status: "error" as const }
              : message,
          ),
        );
        const latest = next[next.length - 1];
        if (latest) {
          patchGroup(groupId, {
            last_message: latest.mensaje,
            last_message_at: latest.creado_en,
          });
        }
        setError(apiMessage(lastError, "No se pudo enviar el mensaje."));

        window.setTimeout(() => {
          void loadMessages(groupId, { quiet: true, incremental: false });
        }, 1_500);
      } finally {
        sendingIdsRef.current.delete(clientId);
      }
    },
    [loadMessages, patchGroup, scope, updateCachedMessages],
  );

  const send = () => {
    if (!selected || (!text.trim() && !file)) return;

    const groupId = selected.id;
    const messageText = text.trim();
    const currentFile = file;
    const clientId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    const clientOrder = Date.now() + Math.random();

    const optimistic: GroupChatMessage = {
      id: -Date.now(),
      from: "me",
      emisor_id: null,
      emisor_nombre: "Tú",
      mensaje: messageText || (currentFile ? "Archivo adjunto" : ""),
      creado_en: new Date().toISOString(),
      adjunto: currentFile
        ? {
            id: -Date.now(),
            nombre: currentFile.name,
            mime: currentFile.type || null,
            size: currentFile.size,
          }
        : null,
      client_id: clientId,
      client_order: clientOrder,
      status: "sending",
    };

    setText("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    shouldStickToBottomRef.current = true;
    forceScrollToBottomRef.current = true;
    updateCachedMessages(groupId, (current) => mergeMessages(current, [optimistic]));
    patchGroup(groupId, {
      last_message: optimistic.mensaje,
      last_message_at: optimistic.creado_en,
      unread_count: 0,
    });
    setError(null);

    void sendPendingMessage(
      groupId,
      clientId,
      clientOrder,
      messageText,
      currentFile,
    );
  };

  const saveManager = async () => {
    if (!selected || managerSaving) return;

    setManagerSaving(true);
    setError(null);
    try {
      if (groupName.trim() && groupName.trim() !== selected.nombre) {
        await renameGroupChat(selected.id, groupName.trim());
      }
      await setGroupMessaging(
        selected.id,
        blockMode,
        blockMode === "until" ? blockedUntil : null,
      );
      setManagerOpen(false);
      await refreshGroups(true);
    } catch (requestError) {
      if (!isRequestCancellation(requestError)) {
        setError(apiMessage(requestError, "No se pudo actualizar el grupo."));
      }
    } finally {
      setManagerSaving(false);
    }
  };

  const addMember = async (groupId: number, userId: number) => {
    if (memberUpdating) return;
    setMemberUpdating(true);
    try {
      await addGroupMember(groupId, userId);
      await refreshGroups(true);
    } catch (requestError) {
      if (!isRequestCancellation(requestError)) {
        setError(apiMessage(requestError, "No se pudo añadir el usuario al grupo."));
      }
    } finally {
      setMemberUpdating(false);
    }
  };

  const availableMembers = useMemo(
    () =>
      allUsers.filter(
        (user) => selected && !selected.members.some((member) => member.id === user.id),
      ),
    [allUsers, selected],
  );

  return (
    <>
      {active && (
        <div className="flex min-h-0 flex-1 flex-col bg-[#f8fbfc]">
          {floating ? (
            <div className="flex shrink-0 items-center gap-2 border-b border-[#edf1f3] bg-white px-3 py-2">
              <button
                type="button"
                onClick={onToggleConversationList}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-emerald-100 bg-white text-emerald-700 shadow-[0_6px_16px_rgba(16,185,129,.06)] transition hover:border-emerald-200 hover:bg-emerald-50"
                title={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
                aria-label={showConversationList ? "Ocultar conversaciones" : "Mostrar conversaciones"}
              >
                {showConversationList ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
              </button>

              <span className="min-w-0 flex-1 truncate text-sm font-black text-slate-950">
                {selected?.nombre ?? (loadingGroups ? "Cargando conversaciones..." : "Selecciona un grupo")}
              </span>

              {selected?.is_creator && (
                <button
                  type="button"
                  onClick={() => void openManager()}
                  disabled={openingManager || managerSaving}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-emerald-100 bg-white text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Configurar grupo"
                >
                  {openingManager ? <Loader2 size={14} className="animate-spin" /> : <Settings2 size={14} />}
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 border-b border-[#edf1f3] bg-white px-6 py-5">
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-2xl border border-emerald-100 bg-[linear-gradient(135deg,#ecfdf5_0%,#f0fdfa_100%)] text-emerald-700 shadow-[0_14px_34px_rgba(16,185,129,.10)]">
                  <MessageCircle size={19} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2 text-sm font-black text-slate-950">
                    Chat con
                    <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                      {selected?.nombre ?? (loadingGroups ? "Cargando conversaciones..." : "Selecciona un grupo")}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,.13)]" />
                    {selected ? `${selected.members.length} miembro(s)` : "Grupo"}
                  </div>
                </div>
              </div>

              {selected?.is_creator && (
                <button
                  type="button"
                  onClick={() => void openManager()}
                  disabled={openingManager || managerSaving}
                  className="inline-flex items-center gap-2 rounded-2xl border border-[#dfe8e4] bg-white px-3 py-2.5 text-xs font-black text-slate-600 shadow-[0_8px_22px_rgba(15,23,42,.045)] transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {openingManager ? <Loader2 size={15} className="animate-spin" /> : <Settings2 size={15} />}
                  Configurar grupo
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="shrink-0 border-b border-rose-100 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700">
              {error}
            </div>
          )}

          {!selected ? (
            <div className="grid min-h-0 flex-1 place-items-center p-6 text-center">
              <div>
                <MessageCircle size={28} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm font-bold text-slate-400">Selecciona un grupo.</p>
              </div>
            </div>
          ) : (
            <>
              {!selected.messages_enabled && (
                <div className="flex shrink-0 items-center gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-800">
                  <Lock size={14} />
                  {selected.blocked_reason ?? "El envío de mensajes está temporalmente desactivado."}
                </div>
              )}

              <div
                ref={messagesScrollRef}
                onScroll={handleMessagesScroll}
                className={`dashboard-chat-scroll overflow-y-auto bg-[#f8fbfc] p-5 ${
                  floating ? "min-h-0 flex-1" : "h-[500px] max-h-[500px] shrink-0"
                }`}
              >
                {loadingMessages && messages.length === 0 ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-sm font-bold text-slate-400">
                    <Loader2 size={16} className="animate-spin text-emerald-600" />
                    Cargando conversación...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="grid min-h-[220px] place-items-center text-center">
                    <div>
                      <MessageCircle size={30} className="mx-auto text-slate-300" />
                      <p className="mt-3 text-sm font-bold text-slate-400">Todavía no hay mensajes.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((message) => {
                      const isMe = message.from === "me";
                      const attachment = message.adjunto;

                      return (
                        <div
                          key={message.client_id ?? message.id}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={[
                              "max-w-[82%] rounded-[24px] px-4 py-3 text-sm font-semibold shadow-[0_10px_26px_rgba(15,23,42,.06)]",
                              isMe
                                ? "border border-emerald-200 bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_14px_34px_rgba(16,185,129,.16)]"
                                : "border border-[#e7ecef] bg-white text-slate-700",
                            ].join(" ")}
                          >
                            <div
                              className={`mb-1 text-[10px] font-black uppercase tracking-[0.08em] ${
                                isMe ? "text-white/70" : "text-emerald-700"
                              }`}
                            >
                              {message.emisor_nombre || (isMe ? "Tú" : "Usuario")}
                            </div>

                            <div>{message.mensaje}</div>

                            {attachment && (
                              <div
                                className={[
                                  "mt-3 w-full max-w-[360px] rounded-2xl border px-3 py-3",
                                  isMe
                                    ? "border-white/20 bg-white/15 text-white"
                                    : "border-emerald-100 bg-emerald-50/70 text-slate-600",
                                ].join(" ")}
                              >
                                <div className="flex items-center gap-3">
                                  <span
                                    className={[
                                      "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
                                      isMe ? "bg-white/15" : "bg-white text-emerald-700",
                                    ].join(" ")}
                                  >
                                    <FileText size={17} />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-black" title={attachment.nombre}>
                                      {attachment.nombre}
                                    </span>
                                    <span className={`text-[11px] font-bold ${isMe ? "text-white/65" : "text-slate-400"}`}>
                                      {formatFileSize(attachment.size)}
                                    </span>
                                  </span>
                                </div>

                                {attachment.id > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => void downloadGroupAttachment(scope, attachment)}
                                    className={[
                                      "mt-2 inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[11px] font-black transition",
                                      isMe
                                        ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
                                        : "border-emerald-100 bg-white text-emerald-700 hover:bg-emerald-50",
                                    ].join(" ")}
                                  >
                                    <Download size={13} />
                                    Descargar
                                  </button>
                                ) : (
                                  <div className={`mt-2 text-[11px] font-bold ${isMe ? "text-white/65" : "text-slate-400"}`}>
                                    Enviando archivo...
                                  </div>
                                )}
                              </div>
                            )}

                            <div className={`mt-1 text-[10px] font-bold ${isMe ? "text-white/65" : "text-slate-400"}`}>
                              {timeLabel(message.creado_en)}
                              {message.status === "sending" && isMe && (
                                <div className="mt-0.5 text-[10px] font-bold text-white/55">Enviando...</div>
                              )}
                              {message.status === "error" && isMe && (
                                <div className="mt-0.5 text-[10px] font-bold text-rose-100">Pendiente de reintento</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={`shrink-0 border-t border-[#edf1f3] bg-white ${floating ? "p-3" : "p-5"}`}>
                {file && (
                  <div className="mb-3 flex items-center justify-between rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-slate-700 shadow-[0_8px_20px_rgba(16,185,129,.06)]">
                    <span className="truncate">Adjunto: {file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="grid h-7 w-7 place-items-center rounded-full bg-white text-slate-500 shadow-[0_6px_14px_rgba(15,23,42,.05)] hover:text-rose-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                <div className="flex items-end gap-3 rounded-[28px] border border-[#dfe8e4] bg-white p-3 shadow-[0_18px_44px_rgba(15,23,42,.08)] transition-all duration-300 focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100">
                  <label
                    className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 transition-colors duration-300 ${
                      selected.messages_enabled ? "cursor-pointer hover:bg-emerald-100" : "cursor-not-allowed opacity-45"
                    }`}
                    title="Adjuntar archivo"
                  >
                    <Paperclip size={17} />
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      disabled={!selected.messages_enabled}
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                    />
                  </label>

                  <textarea
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    disabled={!selected.messages_enabled}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        void send();
                      }
                    }}
                    placeholder={
                      selected.messages_enabled ? "Escribe un mensaje..." : "Mensajes desactivados"
                    }
                    className="max-h-28 min-h-11 min-w-0 flex-1 resize-none bg-transparent px-2 py-2.5 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:text-slate-400"
                  />

                  <button
                    type="button"
                    onClick={() => void send()}
                    disabled={!selected.messages_enabled || (!text.trim() && !file)}
                    className="inline-flex h-11 shrink-0 items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-4 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-colors duration-300 hover:shadow-[0_24px_52px_rgba(16,185,129,.30)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send size={16} />
                    {!floating && "Enviar"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {admin && createOpen && (
        <GroupModal
          title="Crear grupo"
          onClose={() => {
            if (!creating) {
              setCreateOpen(false);
              setCreateBusy(false);
            }
          }}
          onSave={() => void create()}
          saveLabel="Crear grupo"
          saving={creating}
          saveDisabled={!groupName.trim() || selectedUsers.length === 0}
          floating={floating}
        >
          <label className="block">
            <span className="text-xs font-black uppercase text-slate-500">Nombre</span>
            <input
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              disabled={creating}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold disabled:bg-slate-50"
            />
          </label>
          <div>
            <div className="mb-2 text-xs font-black uppercase text-slate-500">
              Participantes de cualquier rol
            </div>
            <UserSelector
              users={allUsers}
              selected={selectedUsers}
              onChange={setSelectedUsers}
              disabled={creating}
            />
          </div>
        </GroupModal>
      )}

      {admin && managerOpen && selected && (
        <GroupModal
          title={`Gestionar · ${selected.nombre}`}
          onClose={() => {
            if (!managerSaving) setManagerOpen(false);
          }}
          onSave={() => void saveManager()}
          saveLabel="Guardar"
          saving={managerSaving}
          floating={floating}
        >
          <label className="block">
            <span className="text-xs font-black uppercase text-slate-500">Nombre</span>
            <input
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              disabled={managerSaving}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold disabled:bg-slate-50"
            />
          </label>

          <div>
            <div className="mb-2 text-xs font-black uppercase text-slate-500">Miembros</div>
            <div className="space-y-2">
              {selected.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2"
                >
                  <div>
                    <div className="text-sm font-black text-slate-800">{member.nombre}</div>
                    <div className="text-[11px] font-semibold capitalize text-slate-400">
                      {roleLabel(member.rol)}
                    </div>
                  </div>
                  {member.id !== selected.creador_id && (
                    <button
                      type="button"
                      onClick={() => setConfirm({ kind: "remove-member", group: selected, user: member })}
                      disabled={memberUpdating || managerSaving}
                      className="grid h-8 w-8 place-items-center rounded-lg text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <UserMinus size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {availableMembers.length > 0 && (
              <select
                defaultValue=""
                disabled={memberUpdating || managerSaving}
                onChange={(event) => {
                  const id = Number(event.target.value);
                  if (id) void addMember(selected.id, id);
                  event.target.value = "";
                }}
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold disabled:bg-slate-50"
              >
                <option value="">Añadir usuario...</option>
                {availableMembers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.nombre} · {roleLabel(user.rol)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <div className="mb-2 text-xs font-black uppercase text-slate-500">
              Envío de mensajes
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              {(
                [
                  ["active", "Activo"],
                  ["indefinite", "Desactivado"],
                  ["until", "Hasta fecha"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setBlockMode(value)}
                  disabled={managerSaving}
                  className={`rounded-xl border px-3 py-2 text-xs font-black disabled:cursor-not-allowed disabled:opacity-50 ${
                    blockMode === value
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 text-slate-500"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {blockMode === "until" && (
              <input
                type="datetime-local"
                value={blockedUntil}
                onChange={(event) => setBlockedUntil(event.target.value)}
                disabled={managerSaving}
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold disabled:bg-slate-50"
              />
            )}
          </div>

          <button
            type="button"
            onClick={() => setConfirm({ kind: "delete-group", group: selected })}
            disabled={managerSaving}
            className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-black text-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 size={14} />
            Eliminar grupo
          </button>
        </GroupModal>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === "delete-group" ? "Eliminar grupo" : "Eliminar miembro"}
        message={
          confirm?.kind === "delete-group"
            ? `Se eliminará «${confirm.group.nombre}» y todo su historial grupal. El chat individual no se verá afectado.`
            : confirm
              ? `¿Eliminar a ${confirm.user.nombre} de «${confirm.group.nombre}»?`
              : undefined
        }
        confirmLabel="Eliminar"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;

          if (confirm.kind === "delete-group") {
            void deleteGroupChat(confirm.group.id)
              .then(async () => {
                setManagerOpen(false);
                selectGroup(null);
                delete messagesCacheRef.current[confirm.group.id];
                await refreshGroups(true);
              })
              .catch((requestError) => {
                if (!isRequestCancellation(requestError)) {
                  setError(apiMessage(requestError, "No se pudo eliminar el grupo."));
                }
              })
              .finally(() => setConfirm(null));
            return;
          }

          void removeGroupMember(confirm.group.id, confirm.user.id)
            .then(() => refreshGroups(true))
            .catch((requestError) => {
              if (!isRequestCancellation(requestError)) {
                setError(apiMessage(requestError, "No se pudo eliminar el miembro del grupo."));
              }
            })
            .finally(() => setConfirm(null));
        }}
      />
    </>
  );
}

function UserSelector({
  users,
  selected,
  onChange,
  disabled = false,
}: {
  users: GroupChatUser[];
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="max-h-56 space-y-2 overflow-auto rounded-xl border border-slate-200 p-2">
      {users.map((user) => {
        const checked = selected.includes(user.id);
        return (
          <label
            key={user.id}
            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50"
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, user.id]
                    : selected.filter((id) => id !== user.id),
                )
              }
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold text-slate-800">{user.nombre}</span>
              <span className="block truncate text-[11px] font-semibold text-slate-400">
                {roleLabel(user.rol)} · {user.correo}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function GroupModal({
  title,
  children,
  onClose,
  onSave,
  saveLabel,
  saving = false,
  saveDisabled = false,
  floating = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  onSave: () => void;
  saveLabel: string;
  saving?: boolean;
  saveDisabled?: boolean;
  floating?: boolean;
}) {
  const modal = (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-slate-950/35 p-3 backdrop-blur-sm sm:p-4">
      <div className="flex min-h-full items-center justify-center">
        <div
          className={[
            "flex w-full max-w-xl flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-2xl",
            floating ? "max-h-[calc(100dvh-1.5rem)]" : "max-h-[90vh]",
          ].join(" ")}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
            <h3 className="text-lg font-black text-slate-950">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X size={16} />
            </button>
          </div>
          <div className="dashboard-chat-scroll min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-5">
            {children}
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-black text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || saveDisabled}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0b4a2f] px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
